import { DynamicObjectInterface } from '../../sdk/type';
import { LoggerService } from '../../ui/utils';
import { ContentStackService, ContentStackConsentService, ContentStackArticleService } from './contentStackService';

export const fetchContent = async (contentType: string): Promise<DynamicObjectInterface | null> => {
  try {
    const result = await ContentStackService.getContentStackDataConfig();
    return result.data && result.data.length > 0 ? result.data[0][contentType] : null;
  } catch (error) {
    LoggerService.log('Failed to fetch content:', error);
    return null;
  }
};

export const fetchConsentContent = async (): Promise<DynamicObjectInterface | null> => {
  try {
    const result = await ContentStackConsentService.getContentStackDataConsent();
    return result.data && result.data.length > 0 ? result.data[0] : null;
  } catch (error) {
    LoggerService.log('Failed to fetch content:', error);
    return null;
  }
};

export const fetchLatestCategory = async (
  numberOfItem: number,
  categoryId?: string,
  subCategoryId?: string,
): Promise<DynamicObjectInterface | null> => {
  try {
    const result = await ContentStackArticleService.getArticleByCategoryId(numberOfItem, categoryId, subCategoryId);
    return result.data && result.data.length > 0 ? result.data : null;
  } catch (error) {
    LoggerService.log('Failed to fetch content:', error);
    return null;
  }
};

export const cacheContent = (key: string, data: DynamicObjectInterface) => {
  sessionStorage.setItem(key, JSON.stringify(data));
  sessionStorage.setItem(`${key}Timestamp`, new Date().getTime().toString());
};

export const getCachedContent = (key: string): DynamicObjectInterface | null => {
  const cachedContent = sessionStorage.getItem(key);
  if (cachedContent) {
    return JSON.parse(cachedContent);
  }
  return null;
};

export const isContentCacheValid = (key: string, cacheExpirationTime: number): boolean => {
  const cachedContentTimestamp = sessionStorage.getItem(`${key}Timestamp`);

  if (cachedContentTimestamp !== null) {
    return new Date().getTime() - parseInt(cachedContentTimestamp) < cacheExpirationTime;
  }

  return false;
};

export const getContent = async (
  contentType: string,
  cacheKey: string,
  useCache: boolean,
): Promise<DynamicObjectInterface> => {
  let content: DynamicObjectInterface | null = null;
  let cacheExpirationTime = process.env.REACT_CACHE_TIME ? parseInt(process.env.REACT_CACHE_TIME) : 5 * 60 * 1000;

  if (useCache) {
    LoggerService.log("Using cache content - ", cacheKey);
    content = getCachedContent(cacheKey);

    if (!isContentCacheValid(cacheKey, cacheExpirationTime)) {
      sessionStorage.removeItem(cacheKey);
      sessionStorage.removeItem(`${cacheKey}Timestamp`);
      content = await fetchContent(contentType);
      if (content) {
        cacheContent(cacheKey, content);
      }
    }
  } else {
    content = await fetchContent(contentType);
  }

  return content;
};

export const getLatestArticle = async (
  cacheKey: string,
  useCache: boolean,
  numberOfItem: number,
  categoryId?: string,
  subCategoryId?: string,
): Promise<DynamicObjectInterface> => {
  let content: DynamicObjectInterface | null = null;
  let cacheExpirationTime = process.env.REACT_CACHE_TIME ? parseInt(process.env.REACT_CACHE_TIME) : 5 * 60 * 1000;

  if (useCache) {
    content = getCachedContent(cacheKey);

    if (!isContentCacheValid(cacheKey, cacheExpirationTime)) {
      sessionStorage.removeItem(cacheKey);
      sessionStorage.removeItem(`${cacheKey}Timestamp`);
      content = await fetchLatestCategory(numberOfItem, categoryId, subCategoryId);
      if (content) {
        cacheContent(cacheKey, content);
      }
    }
  } else {
    content = await fetchLatestCategory(numberOfItem, categoryId, subCategoryId);
  }

  return content || {};
};

export const getConsent = async (cacheKey: string, useCache: boolean): Promise<DynamicObjectInterface> => {
  let content: DynamicObjectInterface | null = null;
  let cacheExpirationTime = process.env.REACT_CACHE_TIME ? parseInt(process.env.REACT_CACHE_TIME) : 5 * 60 * 1000;

  if (useCache) {
    content = getCachedContent(cacheKey);
    if (!isContentCacheValid(cacheKey, cacheExpirationTime)) {
      sessionStorage.removeItem(cacheKey);
      sessionStorage.removeItem(`${cacheKey}Timestamp`);
      content = await fetchConsentContent();
      if (content) {
        cacheContent(cacheKey, content);
      }
    }
  } else {
    content = await fetchConsentContent();
  }

  return content || {};
};
